// src/components/orders/orderTimeline.js

import React from 'react';

// ─── Maps every possible Shiprocket + order status to a display label ─────────
const STATUS_LABEL = {
  // Order statuses
  PENDING: 'Order Placed',
  CONFIRMED: 'Order Confirmed',
  PROCESSING: 'Processing',
  SHIPPED: 'Handed to Courier',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  RETURNED: 'Returned',
  RETURN_REQUESTED: 'Return Requested',

  // Shiprocket shipment statuses
  PICKUP_SCHEDULED: 'Pickup Scheduled',
  PICKUP_FAILED: 'Pickup Failed',
  PICKED_UP: 'Picked Up',
  IN_TRANSIT: 'In Transit',
  REACHED_DESTINATION_HUB: 'Reached Destination Hub',
  OUT_FOR_DELIVERY: 'Out for Delivery',
  UNDELIVERED: 'Delivery Attempted',
  ATTEMPTED_DELIVERY: 'Delivery Attempted',
  DELAYED: 'Delayed',
  RETURNED_TO_ORIGIN: 'Returned to Origin',
  LOST: 'Shipment Lost',
  DAMAGED: 'Shipment Damaged',

  // Payment statuses that appear in order timeline
  PAYMENT_COMPLETED: 'Payment Confirmed',
  PAYMENT_PENDING: 'Payment Pending',
  PAYMENT_FAILED: 'Payment Failed',
  REFUND_PROCESSED: 'Refund Processed',
  SHIPPING_UPDATED: 'Shipping Updated',
};

// ─── Dot colour per status ────────────────────────────────────────────────────
const getDotColor = (status) => {
  switch ((status || '').toUpperCase()) {
    case 'DELIVERED':
      return 'bg-green-500';

    case 'CONFIRMED':
    case 'PAYMENT_COMPLETED':
    case 'PICKED_UP':
      return 'bg-blue-500';

    case 'PENDING':
    case 'PENDING_PAYMENT':
      return 'bg-yellow-400';

    case 'PROCESSING':
    case 'PICKUP_SCHEDULED':
    case 'IN_TRANSIT':
    case 'REACHED_DESTINATION_HUB':
    case 'SHIPPED':
      return 'bg-indigo-500';

    case 'OUT_FOR_DELIVERY':
      return 'bg-purple-500';

    case 'CANCELLED':
    case 'PICKUP_FAILED':
    case 'PAYMENT_FAILED':
    case 'LOST':
    case 'DAMAGED':
      return 'bg-red-500';

    case 'RETURNED':
    case 'RETURN_REQUESTED':
    case 'RETURNED_TO_ORIGIN':
      return 'bg-orange-500';

    case 'DELAYED':
    case 'UNDELIVERED':
    case 'ATTEMPTED_DELIVERY':
      return 'bg-yellow-600';

    case 'REFUND_PROCESSED':
      return 'bg-teal-500';

    default:
      return 'bg-gray-400';
  }
};

// ─── Icon per status ──────────────────────────────────────────────────────────
const getStatusIcon = (status) => {
  switch ((status || '').toUpperCase()) {
    case 'PENDING':           return '🛒';
    case 'CONFIRMED':         return '✔️';
    case 'PROCESSING':        return '⚙️';
    case 'PICKUP_SCHEDULED':  return '📅';
    case 'PICKUP_FAILED':     return '❌';
    case 'PICKED_UP':         return '📦';
    case 'SHIPPED':           return '🚚';
    case 'IN_TRANSIT':        return '🚚';
    case 'REACHED_DESTINATION_HUB': return '🏭';
    case 'OUT_FOR_DELIVERY':  return '🏃';
    case 'DELIVERED':         return '✅';
    case 'UNDELIVERED':
    case 'ATTEMPTED_DELIVERY': return '🔔';
    case 'DELAYED':           return '⏳';
    case 'CANCELLED':         return '🚫';
    case 'RETURNED':
    case 'RETURN_REQUESTED':
    case 'RETURNED_TO_ORIGIN': return '↩️';
    case 'LOST':              return '❓';
    case 'DAMAGED':           return '⚠️';
    case 'PAYMENT_COMPLETED': return '💳';
    case 'REFUND_PROCESSED':  return '💰';
    case 'SHIPPING_UPDATED':  return '📋';
    default:                  return '📍';
  }
};

// ─── Format timestamp ─────────────────────────────────────────────────────────
const formatDate = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ─── Normalise events from both sources into a single shape ──────────────────
//
// Order timeline event shape:  { status, timestamp, note, location }
// Shiprocket activity shape:   { date, activity, location, status }
//
const normaliseEvents = (orderTimeline = [], shipmentTimeline = []) => {
  const orderEvents = (orderTimeline || []).map(e => ({
    status: e.status,
    label: STATUS_LABEL[e.status] || e.status,
    timestamp: e.timestamp,
    note: e.note || '',
    location: e.location || '',
    source: 'order'
  }));

  const shipmentEvents = (shipmentTimeline || []).map(e => ({
    // shipment timeline uses either e.status (DB) or e.activity (Shiprocket live)
    status: e.status || e.activity || 'IN_TRANSIT',
    label: STATUS_LABEL[(e.status || '').toUpperCase()]
      || e.activity
      || e.status
      || 'Update',
    timestamp: e.timestamp || e.date,
    note: e.description || e.courierRemarks || '',
    location: e.location || '',
    source: 'shipment'
  }));

  // Merge and sort — newest first
  const all = [...orderEvents, ...shipmentEvents];
  all.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  // Deduplicate: if an order event and shipment event have the same status
  // within 60 seconds of each other, keep only the shipment one (more detail)
  const deduped = [];
  all.forEach(ev => {
    const existing = deduped.find(d =>
      d.status === ev.status &&
      Math.abs(new Date(d.timestamp) - new Date(ev.timestamp)) < 60000
    );
    if (!existing) {
      deduped.push(ev);
    } else if (ev.source === 'shipment') {
      // Replace with shipment version — it has richer data
      const idx = deduped.indexOf(existing);
      deduped[idx] = ev;
    }
  });

  return deduped;
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * OrderTimeline
 *
 * Props:
 *   timeline         {Array}  Order timeline array from order model
 *   shipmentTimeline {Array}  Timeline array from shipment model OR
 *                             trackingHistory array from Shiprocket live tracking
 *
 * Either prop alone works — pass both for the richest view.
 *
 * Usage:
 *   <OrderTimeline
 *     timeline={order.timeline}
 *     shipmentTimeline={shipment?.timeline || trackingData?.trackingHistory}
 *   />
 */
const OrderTimeline = ({ timeline, shipmentTimeline }) => {
  const events = normaliseEvents(timeline, shipmentTimeline);

  if (events.length === 0) {
    return (
      <div className="text-gray-500 text-center py-6 text-sm">
        No timeline events available yet.
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {events.map((event, index) => (
        <div key={index} className="flex items-start gap-3">
          {/* Left: dot + connector line */}
          <div className="flex flex-col items-center flex-shrink-0 pt-0.5">
            <div className={`w-3 h-3 rounded-full ${getDotColor(event.status)} flex-shrink-0`} />
            {index < events.length - 1 && (
              <div className="w-px bg-gray-200 mt-1" style={{ height: '2.5rem' }} />
            )}
          </div>

          {/* Right: content */}
          <div className="pb-4 flex-1 min-w-0">
            {/* Status label + icon */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm">{getStatusIcon(event.status)}</span>
              <span className="text-sm font-semibold text-gray-800">
                {event.label}
              </span>
              {/* Shiprocket badge to distinguish live updates */}
              {event.source === 'shipment' && (
                <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-medium">
                  Courier Update
                </span>
              )}
            </div>

            {/* Timestamp */}
            {event.timestamp && (
              <p className="text-xs text-gray-400 mt-0.5">
                {formatDate(event.timestamp)}
              </p>
            )}

            {/* Note / description */}
            {event.note && (
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                {event.note}
              </p>
            )}

            {/* Location */}
            {event.location && (
              <p className="text-xs text-gray-400 mt-0.5">
                📍 {event.location}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default OrderTimeline;